import { useCallback } from 'react';

import qs from 'qs';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';
import { hardNavigate } from '#src/app/pages/psf/share/shared/utils';
import { useIsMountedRef } from '#src/app/shared/hooks/lifecycle';
import { useQueryParamValue } from '#src/app/shared/hooks/location';
import { useCancelableNavigate } from '#src/app/shared/hooks/navigate';
import type { ShareChannel } from '#src/app/shared/hooks/share';
import { canRedirectAfterShare } from '#src/app/shared/share/webviewRedirect';

import { usePromoteSlideUpExperiment } from '../../usePromoteSlideUpExperiment';
import { useShouldShowSharingGoalFlow } from '../../useShouldShowSharingGoalFlow';

type SecondScreenParams = {
	shortDisplayName: string | undefined;
	sourceLocation: string | undefined;
	comboStaticAmount: string | undefined;
	comboStaticCurrency: string | undefined;
};

const generateInitialShareUrl = (
	slug: string,
	{ shortDisplayName, sourceLocation, comboStaticAmount, comboStaticCurrency }: SecondScreenParams,
) =>
	`/p/${encodeURIComponent(slug)}/psf/share${qs.stringify(
		{
			share: 1,
			...(shortDisplayName && { short_display_name: shortDisplayName }),
			...(sourceLocation && { source_location: sourceLocation }),
			...(comboStaticAmount && { psf_combo_static_amount: comboStaticAmount }),
			...(comboStaticCurrency && { psf_combo_static_currency: comboStaticCurrency }),
		},
		{ addQueryPrefix: true },
	)}`;

const generateDiscoverUrl = (slug: string, guest: string | undefined) =>
	`/p/${encodeURIComponent(slug)}/d${qs.stringify(
		{
			source_location: 'combo_psf',
			...(['new', 'existing'].includes(guest || '') ? { from_guest_psf: '1' } : undefined),
		},
		{ addQueryPrefix: true },
	)}`;

export function useRedirect() {
	const {
		data: {
			initial,
			petition: { slug },
		},
	} = usePsfSharePageData();
	const shouldShowSharingGoalFlow = useShouldShowSharingGoalFlow();
	const { arm, awaitTreatment, claimNavigation, restoreEpoch } = usePromoteSlideUpExperiment();
	const isMountedRef = useIsMountedRef();
	const { cancelableNavigate } = useCancelableNavigate();
	const shortDisplayName = useQueryParamValue('short_display_name');
	const sourceLocation = useQueryParamValue('source_location');
	const comboStaticAmount = useQueryParamValue('psf_combo_static_amount');
	const comboStaticCurrency = useQueryParamValue('psf_combo_static_currency');
	const guest = useQueryParamValue('guest');
	const {
		errorReporter,
		userAgent: { isWebview },
	} = useUtilityContext();

	return useCallback(
		(channel: ShareChannel): void => {
			if (channel === 'copy') return;

			if (!canRedirectAfterShare({ channel, isWebview })) return;

			if (initial && !shouldShowSharingGoalFlow) {
				cancelableNavigate({
					eventName: 'shareChannelSelectedRedirect',
					eventConfig: { selectedChannel: channel },
					redirectUrl: generateInitialShareUrl(slug, {
						shortDisplayName,
						sourceLocation,
						comboStaticAmount,
						comboStaticCurrency,
					}),
					navigateOptions: {
						// forcing external mode to prevent a client-side routing on the same path
						// which would result in a loading state and a non-reset page state
						// TODO rework the PSF share urls to use different paths
						forceMode: 'external',
					},
					deferWhileHidden: true,
				});
				return;
			}

			const navigateToDiscover = () =>
				cancelableNavigate({
					eventName: 'shareChannelSelectedRedirect',
					eventConfig: { selectedChannel: channel },
					redirectUrl: generateDiscoverUrl(slug, guest),
					deferWhileHidden: true,
				});

			if (arm === 'ineligible') {
				navigateToDiscover();
				return;
			}

			// Both arms are treated before they diverge, so the experiment measures a share on its screen
			// rather than only the arm that stays on the page.
			void (async () => {
				try {
					const epoch = restoreEpoch();
					await awaitTreatment();

					// A restore while waiting means the share that started this belongs to a visit the
					// supporter already left.
					if (!isMountedRef.current || restoreEpoch() !== epoch) return;
					// The slide-up replaces this redirect: the supporter must stay for the footer to offer it.
					if (arm === 'variant') return;
					// This redirect is automatic, so it stands down once a direct tap has already taken the
					// supporter somewhere. Claimed immediately before navigating, since anything awaited
					// after the claim could let a tap slip in behind it.
					if (!claimNavigation()) return;

					navigateToDiscover();
				} catch (error) {
					void errorReporter.report({
						error: error as Error,
						context: { reason: 'psf_share_promote_slide_up_redirect' },
					});
					// The share already happened, so the supporter still has to reach the next page.
					hardNavigate(generateDiscoverUrl(slug, guest));
				}
			})();
		},
		[
			initial,
			arm,
			awaitTreatment,
			claimNavigation,
			restoreEpoch,
			errorReporter,
			isMountedRef,
			shouldShowSharingGoalFlow,
			shortDisplayName,
			sourceLocation,
			comboStaticAmount,
			comboStaticCurrency,
			guest,
			slug,
			cancelableNavigate,
			isWebview,
		],
	);
}
